// Standalone voice-note capture — record → transcribe → save, no review/suggestion step (unlike
// NotePanel.tsx's Note Taker, which analyzes for tags/follow-ups). Reuses the same mic-capture +
// streaming-STT plumbing (useAudioCapture via useNoteRecorder, realtime-gateway, Google STT, S3
// audio URL), stripped of the SAM/chat-specific wiring (startRev/stopRev driven by voice commands,
// live-agent mute/unmute, history menu) since this runs inside a plain workspace card, not a chat
// surface. Used for the assessment "Voice note" action — stop just saves the transcript as-is.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNoteRecorder } from '@/hooks/useNoteRecorder';
import { useCreateNoteMutation, type Note } from '@/redux/internal-services/notesApi';
import { NoteWaveRing } from '@/components/notes/NoteWaveRing';
import { CaptionBox } from '@/components/chat/CaptionBox';

type Phase = 'idle' | 'recording' | 'processing' | 'error';

const MicGlyph = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z" fill="currentColor" />
    <path d="M19 11a7 7 0 0 1-14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function newDraftId(): string {
  try { return crypto.randomUUID(); } catch { return `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

interface VoiceNoteRecorderProps {
  contextType: string;
  entityId: string;
  onSaved: (note: Note) => void;
  onCancel: () => void;
}

export function VoiceNoteRecorder({ contextType, entityId, onSaved, onCancel }: VoiceNoteRecorderProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const draftIdRef = useRef<string>('');

  const [createNote] = useCreateNoteMutation();

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);
  useEffect(() => () => stopTimer(), [stopTimer]);

  // Transcription timeout — mirrors NotePanel.tsx: if STT doesn't resolve within 45s (dropped
  // connection, silence with no final event), bail out to an error state instead of leaving the
  // user staring at "Transcribing…" forever with no way out.
  const procTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (phase !== 'processing') {
      if (procTimeoutRef.current) { clearTimeout(procTimeoutRef.current); procTimeoutRef.current = null; }
      return;
    }
    procTimeoutRef.current = setTimeout(() => {
      setErrorMsg('Transcription timed out — please try again.');
      setPhase('error');
    }, 45_000);
    return () => {
      if (procTimeoutRef.current) { clearTimeout(procTimeoutRef.current); procTimeoutRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const recorder = useNoteRecorder({
    onFinal: async ({ transcript, audioUrl }) => {
      try {
        const created = await createNote({
          source: 'voice',
          final_transcript: transcript,
          clean_transcript: transcript,
          audio_url: audioUrl || undefined,
          context: { context_type: contextType, entity_id: entityId },
          idempotency_key: draftIdRef.current,
        }).unwrap();
        onSaved(created);
      } catch (e) {
        const msg = (e as { data?: { message?: string } })?.data?.message || 'Something went wrong.';
        setErrorMsg(msg);
        setPhase('error');
      }
    },
  });

  const startVoice = useCallback(async () => {
    setErrorMsg(null);
    try {
      const id = newDraftId();
      draftIdRef.current = id;
      await recorder.start(id);
      setElapsed(0);
      stopTimer();
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      setPhase('recording');
    } catch {
      setErrorMsg('Something went wrong.');
      setPhase('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopTimer]);

  const stopVoice = useCallback(() => {
    stopTimer();
    recorder.stop();
    setPhase('processing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopTimer]);

  const cancelVoice = useCallback(() => {
    stopTimer();
    recorder.cancel();
    onCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopTimer, onCancel]);

  if (phase === 'recording') {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-ui-card p-6 shadow-sm">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-2 self-end text-xs font-medium uppercase tracking-widest text-accent-text">
            <span className="inline-flex h-2.5 w-2.5 animate-pulse rounded-full bg-danger" aria-hidden />
            Recording
          </div>
          <div className="relative flex w-full items-center justify-center">
            <NoteWaveRing levelRef={recorder.levelRef} active height={120} />
            <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-accent-fill text-on-accent ring-1 ring-ui-ring/50 shadow-lg">
              <MicGlyph className="h-6 w-6" />
            </span>
          </div>
          <div className="font-mono text-h2 tracking-widest text-ui-text tabular-nums">{formatTime(elapsed)}</div>
          <div className="min-h-[40px] w-full max-w-md text-center">
            <CaptionBox text={recorder.interim || 'Listening…'} show tone="user" />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={cancelVoice}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-ui-border text-ui-text3 transition hover:bg-ui-hover"
              aria-label="Cancel"
              title="Cancel"
            >
              ✕
            </button>
            <button
              type="button"
              onClick={stopVoice}
              className="flex items-center gap-2 rounded-full bg-accent-fill px-5 py-2.5 text-sm font-semibold text-on-accent shadow-lg transition hover:brightness-110"
            >
              <span className="h-3 w-3 rounded-[2px] bg-white" aria-hidden />
              Stop
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'processing') {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-ui-border bg-ui-card px-4 py-4 text-center">
        <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-accent/25 border-t-accent" aria-hidden />
        <p className="text-sm font-medium text-ui-text">Saving your note…</p>
      </div>
    );
  }

  // idle / error
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-xl border border-ui-border bg-ui-card px-4 py-3 text-center">
      {phase === 'error' && <p className="text-sm text-danger-text">{errorMsg || 'Something went wrong.'}</p>}
      <button
        type="button"
        onClick={() => void startVoice()}
        className="group flex flex-col items-center gap-1 transition hover:opacity-90"
        aria-label="Start recording"
        title="Start recording"
      >
        <div className="relative flex items-center justify-center">
          <NoteWaveRing levelRef={recorder.levelRef} active={false} height={76} />
          <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-accent-fill text-on-accent ring-1 ring-ui-ring/50 shadow-lg transition group-hover:scale-105">
            <MicGlyph className="h-5 w-5" />
          </span>
        </div>
        <span className="text-xs font-medium text-ui-text3">Start recording</span>
      </button>
      <button type="button" onClick={onCancel} className="text-xs text-ui-text3 hover:text-ui-text hover:underline">
        Cancel
      </button>
    </div>
  );
}

export default VoiceNoteRecorder;
